import React, { useContext, useState } from 'react';
import { IdentityContext, setForcedTier, IdentityLevel, TIER_META } from './lib/identity';

const API_BASE = process.env.REACT_APP_API_URL || 'https://norwich-hub.onrender.com';

interface Props {
  marketId:    string;
  currentUser: string;
  /** When true, hide binary-market-specific actions (YES/NO resolve, inject liquidity) */
  multiMode?:  boolean;
}

async function callApi(path: string, body: object): Promise<{ ok: boolean; text: string }> {
  const res  = await fetch(`${API_BASE}${path}`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body),
  });
  const data = await res.json();
  return {
    ok:   res.ok,
    text: res.ok ? (data.message ?? JSON.stringify(data)) : (data.error ?? `HTTP ${res.status}`),
  };
}

interface BtnProps {
  label:    string;
  onClick:  () => void;
  disabled: boolean;
  color:    'green' | 'red' | 'amber' | 'blue' | 'purple';
}

const COLORS: Record<BtnProps['color'], { bg: string; bgDim: string; border: string; text: string; textDim: string }> = {
  green:  { bg: '#14532d', bgDim: '#052e16', border: '#166534', text: '#86efac', textDim: '#166534' },
  red:    { bg: '#450a0a', bgDim: '#1c0606', border: '#7f1d1d', text: '#fca5a5', textDim: '#7f1d1d' },
  amber:  { bg: '#451a03', bgDim: '#1c0a00', border: '#78350f', text: '#fcd34d', textDim: '#78350f' },
  blue:   { bg: '#0c1a3a', bgDim: '#060d1c', border: '#1e3a6e', text: '#93c5fd', textDim: '#1e3a6e' },
  purple: { bg: '#2e1065', bgDim: '#160a30', border: '#4c1d95', text: '#c4b5fd', textDim: '#4c1d95' },
};

function GodBtn({ label, onClick, disabled, color }: BtnProps) {
  const c = COLORS[color];
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        flex: 1,
        padding: '6px 4px',
        borderRadius: 4,
        border: `1px solid ${disabled ? c.bgDim : c.border}`,
        backgroundColor: disabled ? c.bgDim : c.bg,
        color: disabled ? c.textDim : c.text,
        fontSize: 10,
        letterSpacing: '0.08em',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: 'monospace',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
}

export default function GodModePanel({ marketId, currentUser, multiMode = false }: Props): React.JSX.Element | null {
  // IdentityContext is optional here — Classic view renders this panel
  // outside any identity provider. If there's no identity we hide the
  // tier force row entirely.
  const identity              = useContext(IdentityContext);
  const [message, setMessage] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(false);

  // Defence in depth — if an identity context is present, refuse to
  // render unless the caller is SOVEREIGN. Belt-and-brace for the
  // 2026-05-15 fix where the cockpit's chip + panel render was
  // gated; if a future caller forgets the wrapping check, this
  // component still won't render to lower-tier users. Classic view
  // (no provider, identity is null) renders unchanged.
  if (identity && identity.tier !== IdentityLevel.SOVEREIGN) return null;

  async function run(path: string, body: object): Promise<void> {
    setLoading(true);
    setMessage(null);
    try {
      const { ok, text } = await callApi(path, body);
      setIsError(!ok);
      setMessage(text);
    } catch (e) {
      setIsError(true);
      setMessage((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function resolve(winningSide: 'YES' | 'NO'): Promise<void> {
    setLoading(true);
    setMessage(null);
    try {
      const res  = await fetch(`${API_BASE}/api/amm/settle`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ market_id: marketId, winning_side: winningSide }),
      });
      const data = await res.json();
      if (!res.ok) {
        setIsError(true);
        setMessage(data.error ?? `HTTP ${res.status}`);
      } else {
        setIsError(false);
        const amm    = data.amm_settlement;
        const detail = amm
          ? ` · Paid $${amm.total_paid_to_winners?.toFixed(2)} · Residual $${amm.residual_returned_to_architect?.toFixed(2)}`
          : '';
        setMessage(data.message + detail);
      }
    } catch (e) {
      setIsError(true);
      setMessage((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        backgroundColor: '#0d0808',
        border: '1px solid #4a1010',
        borderTop: '2px solid #7f1d1d',
        borderRadius: 8,
        padding: '10px 16px',
        width: '100%',
        maxWidth: 480,
        fontFamily: 'monospace',
        boxShadow: '0 0 20px rgba(127,29,29,0.25)',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <p style={{ color: '#ef4444', fontSize: 9, letterSpacing: '0.22em', margin: 0 }}>
          ⚠ GOD MODE
        </p>
        <p style={{ color: '#4a1010', fontSize: 8, letterSpacing: '0.14em', margin: 0 }}>
          {multiMode ? 'TESTER TOOLS · MULTI-MARKET' : 'ADMIN ONLY · BYPASSES IRON SHIELD'}
        </p>
      </div>

      {/* Row 1 — Resolution (binary markets only) */}
      {!multiMode && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
            <GodBtn label="RESOLVE: YES WINS" onClick={() => resolve('YES')} disabled={loading} color="green" />
            <GodBtn label="RESOLVE: NO WINS"  onClick={() => resolve('NO')}  disabled={loading} color="red"   />
          </div>
          <div style={{ borderTop: '1px solid #2a0a0a', marginBottom: 6 }} />
        </>
      )}

      {/* Row 2 — Tester utilities */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
        <GodBtn
          label={`↺ REFILL ${currentUser.toUpperCase()} $1K`}
          onClick={() => run('/api/god/reset_balance', { user_id: currentUser })}
          disabled={loading}
          color="amber"
        />
        {!multiMode && (
          <GodBtn
            label="INJECT LIQUIDITY"
            onClick={() => run('/api/god/inject_liquidity', { market_id: marketId })}
            disabled={loading}
            color="blue"
          />
        )}
      </div>

      {/* Row 2b — Trust Ladder force. Tester-only; lets the Architect
            flip identity.tier without Civic/Privy/Sumsub wired up.
            Hidden in Classic view where there is no identity context. */}
      {identity && (
        <div style={{ display: 'flex', alignItems: 'stretch', gap: 6, marginBottom: 6 }}>
          <span style={{
            display:       'inline-flex',
            alignItems:    'center',
            padding:       '0 8px',
            color:         '#a78bfa',
            fontSize:      8,
            letterSpacing: '0.18em',
            fontWeight:    700,
            border:        '1px dashed #4c1d95',
            borderRadius:  4,
            whiteSpace:    'nowrap',
          }}>
            ⚡ TRUST
          </span>
          {([
            { tier: IdentityLevel.SEEKER,    label: 'L0 · SEEKER'    },
            { tier: IdentityLevel.OBSERVER,  label: 'L1 · OBSERVER'  },
            { tier: IdentityLevel.CITIZEN,   label: 'L2 · CITIZEN'   },
            { tier: IdentityLevel.SOVEREIGN, label: 'L3 · SOVEREIGN' },
          ] as const).map(opt => {
            const active  = identity.tier === opt.tier;
            const optMeta = TIER_META[opt.tier];
            return (
              <button
                key={opt.tier}
                onClick={() => setForcedTier(opt.tier)}
                style={{
                  flex:            1,
                  padding:         '6px 4px',
                  borderRadius:    4,
                  border:          `1px solid ${active ? optMeta.border : '#1e293b'}`,
                  backgroundColor: active ? optMeta.bg : '#0b1220',
                  color:           active ? optMeta.color : '#475569',
                  fontSize:        9,
                  letterSpacing:   '0.08em',
                  fontFamily:      'monospace',
                  whiteSpace:      'nowrap',
                  cursor:          'pointer',
                  fontWeight:      active ? 700 : 400,
                  boxShadow:       active ? optMeta.shadow : undefined,
                  transition:      'background-color 0.2s, border-color 0.2s, color 0.2s',
                }}>
                {opt.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Row 3 — Mint / Reset */}
      <div style={{ display: 'flex', gap: 8 }}>
        <GodBtn
          label="MINT +$10K"
          onClick={() => run('/api/god/mint', { user_id: currentUser })}
          disabled={loading}
          color="amber"
        />
        {!multiMode && (
          <GodBtn
            label="RESET MARKET"
            onClick={() => run('/api/god/reset_market', { market_id: marketId })}
            disabled={loading}
            color="purple"
          />
        )}
        {multiMode && (
          <GodBtn
            label="RESET MULTI MARKET"
            onClick={() => run('/api/god/multi_reset', { market_id: marketId })}
            disabled={loading}
            color="purple"
          />
        )}
      </div>

      {/* Result message */}
      {message && (
        <p
          style={{
            marginTop: 8,
            fontSize: 10,
            color: isError ? 'rgba(239,68,68,0.8)' : 'rgba(134,239,172,0.8)',
            lineHeight: 1.5,
            margin: '8px 0 0',
          }}
        >
          {message}
        </p>
      )}
    </div>
  );
}
